import React from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import snakecaseKeys from 'snakecase-keys';

import { clickAsLink } from 'helpers/link_helpers';
import MergeRecordsModal from 'components/modals/merge_records_modal';
import ManagedCheckbox from 'components/form/managed_input/checkbox';
import { propertyAttributes } from './research_constants';
import Button from 'shared/button';

import { startCase } from 'lodash';
import { Alert } from 'react-bootstrap-5';

export default class MergeProperties extends React.Component {
  static propTypes = {
    mergeUrl: PropTypes.string.isRequired,
    properties: PropTypes.array.isRequired,
    propertyDefaults: PropTypes.object.isRequired,
    propertyIndex: PropTypes.string.isRequired,
  };

  constructor(props) {
    super(props);
    const keys = {};

    this.state = {
      showMergeModal: false,
      showErrorPane: false,
      keys: keys
    };

    this.errors = [];
    this.requiredFields = [
      'address',
      'city',
      'state',
      'zip'
    ];

    this.blacklistedFields = [
      'createdAt',
      'createdBy',
      'databaseId',
      'id',
      'lastUpdatedAt',
      'lastUpdatedBy',
      'modelType',
      'ownerType',
      'ownerUrl',
      'thumbnailUrl',
      'url'
    ];

    propertyAttributes.map((key) => {
      if (this.shouldDisplayRow(key)) {
        keys[key] = null;
      }
    });
  }

  findSelectedProperty = (id) => {
    return this.props.properties.find(property => property.id === id);
  }

  handleCancelModal = () => {
    this.setState({
      showMergeModal: false
    });
  }

  handleErrorPaneDismiss = () => {
    this.setState({ showErrorPane: false });
  }

  handleSubmit = () => {
    const keys = this.state.keys;
    this.requiredFields.map((key) => {
      if (!this.shouldDisplayRow(key)) {
        keys[key] = this.props.properties[0].id;
      }
    });

    return axios.post(this.props.mergeUrl, {
      mappings: snakecaseKeys(keys),
      property_ids: this.props.properties.map(property => property.id)
    }).then((response) => {
      window.location = response.data.nextUrl;
    }).catch((error) => {
      this.errors = error.response.data.errors;
      this.showErrorPane();
      this.handleCancelModal();
    });
  }

  onSelectAll = (e) => {
    const id = parseInt(e.target.value);
    const keys = {};

    propertyAttributes.map((key) => {
      keys[key] = id;
    });

    this.setState({
      keys: keys
    });
  }

  onSelectKey = (id, key) => {
    this.setState(prevState => ({
      keys: {
        ...prevState.keys,
        [key]: parseInt(id)
      }
    }));
  }

  requiredKeysSelected = () => {
    const required = this.requiredFields.every((key) => {
      if (this.shouldDisplayRow(key)) {
        return this.state.keys[key] != null;
      } else {
        return true;
      }
    });

    return required;
  }

  selectedAll = (id) => {
    const selected = Object.keys(this.state.keys).every((key) => {
      return this.state.keys[key] === parseInt(id);
    });
    return selected;
  }

  shouldDisplayRow(key) {
    const { properties, propertyDefaults } = this.props;
    if (this.blacklistedFields.includes(key)) return false;

    const firstProperty = properties[0];
    const value =
      Array.isArray(firstProperty[key])
        ? firstProperty[key].toString()
        : firstProperty[key];

    let retVal = false;
    const defaultVal = propertyDefaults[key];

    properties.map((property, index) => {
      const val = Array.isArray(property[key]) ? property[key].toString() : property[key];
      let isDefault;

      if (!defaultVal) {
        isDefault = [null, '', '--'].includes(val);
      } else {
        isDefault = val === defaultVal;
      }

      if (index > 0 && (value !== val || !isDefault) && value && val) {
        retVal = true;
      }
    });
    return retVal;
  }

  showErrorPane = () => {
    this.setState({ showErrorPane: true });
  }

  showMergeModal = () => {
    this.setState({
      showMergeModal: true
    });
  }

  showPropertyIndex = (e) => {
    clickAsLink(window, this.props.propertyIndex, e);
  }

  render() {
    const disabled = !this.requiredKeysSelected();
    return (
      <div className="container">
        <div className="d-flex flex-column gap-4 py-4">
          <div>
            <Button.Cancel className="ps-0" onClick={this.showPropertyIndex}>
              &lt; Back to Properties
            </Button.Cancel>
            <h6>
              Merge Properties
            </h6>
          </div>
          <div className="container bg-white p-4 border rounded">
            {this.renderErrorPane()}
            <p className="fs-large">
              In the table below, you will see all conflicting information
              between the properties you have selected to merge. For each
              row, please select which data you would like to keep.
            </p>
            <p className="fs-large">
              Please note that all associations to contacts, comps, activities, etc. will be preserved.
            </p>
            <div>
              <div className="table-container">
                <table className="table table-responsive text-nowrap">
                  <thead>
                    {this.renderHeaderColumns()}
                  </thead>
                  <tbody>
                    {this.renderTableContent()}
                  </tbody>
                </table>
              </div>
              <div className="d-flex justify-content-end mt-4 gap-2">
                <Button.Cancel
                  onClick={this.showPropertyIndex}
                >
                  Cancel
                </Button.Cancel>
                <Button.Primary
                  disabled={disabled}
                  onClick={this.showMergeModal}
                >
                  Merge
                </Button.Primary>
              </div>
              {this.renderMergeModal()}
            </div>
          </div>
        </div>
      </div>
    );
  }

  renderHeaderColumns() {
    const propertyHeaders = [];
    this.props.properties.map((property, index) => {
      propertyHeaders.push(
        <th key={index}>
          <ManagedCheckbox
            checked={this.selectedAll(property.id)}
            label={`Property ${index + 1}`}
            name={`selectAll-${property.id}`}
            onChange={() => {}}
            onValueChange={() => this.onSelectAll({ target: { value: property.id } })}
          />
        </th>
      );
    });

    return (
      <tr>
        <th> Fields </th>
        {propertyHeaders}
        <th> Merged Property </th>
      </tr>
    );
  }

  renderTableContent() {
    const rows = [];
    const keysToDelete = [];
    propertyAttributes.map((key) => {
      if (this.shouldDisplayRow(key)) {
        const propertyCells = [];
        this.props.properties.map((property) => {
          const value = Array.isArray(property[key]) ? property[key].join(', ') : property[key];

          propertyCells.push(
            <td key={property.id + key}>
              <ManagedCheckbox
                checked={this.state.keys[key] === property.id}
                key={key}
                label={value}
                name={`${key}-${property.id}`}
                onChange={() => {}}
                onValueChange={() => this.onSelectKey(property.id, key)}
              />
            </td>
          );
        });
        const id = this.state.keys[key];
        const property = this.findSelectedProperty(id);
        const newVal = id ? property[key] : '';

        rows.push(
          <tr key={key}>
            <td>
              {startCase(key)}
              {this.requiredFields.includes(key) && <span className="required"> * </span> }
            </td>
            {propertyCells}
            <td> {newVal} </td>
          </tr>
        );
      } else keysToDelete.push(key);
    });
    return rows;
  }

  renderMergeModal() {
    return (
      <MergeRecordsModal
        recordType={'Properties'}
        shown={this.state.showMergeModal}
        onCancel={this.handleCancelModal}
        onSubmit={this.handleSubmit}
      />
    );
  }

  renderErrorPane() {
    return (
      <Alert
        dismissible
        show={this.state.showErrorPane}
        variant="danger"
        onClose={this.handleErrorPaneDismiss}
      >
        <Alert.Heading>
          Unable to merge properties
        </Alert.Heading>
        <ul>
          {this.errors.map((error, index) => <li key={index}>{error}</li>)}
        </ul>
      </Alert>
    );
  }
}
